/**
 * LoginPage Component
 * 
 * Provides user authentication interface with email and password login.
 * 
 * Features:
 * - Email and password input validation
 * - Error handling and user feedback
 * - Redirect to upload page on successful login
 * - Link to register page (if invite token available)
 * - Loading states during authentication
 */

import { useState, FormEvent, useEffect, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/auth'
import { LogIn, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import ChangePasswordModal from '../components/ChangePasswordModal'
import { safeReturnUrl } from '../utils/safeReturnUrl'
import { normalizeErrorMessage } from '../utils/dataValidator'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false)
  const loginPasswordRef = useRef('')

  const { login, isAuthenticated, user, accessToken, reloadUser } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get return URL from query params (set by ProtectedRoute) or default to /projects
  const returnUrl = safeReturnUrl(searchParams.get('returnUrl') || searchParams.get('redirect'))

  // Check if user must change password after login
  useEffect(() => {
    if (isAuthenticated && user?.must_change_password && !showPasswordChangeModal) {
      setShowPasswordChangeModal(true)
    }
  }, [isAuthenticated, user, showPasswordChangeModal])

  // If already authenticated and doesn't need to change password, redirect
  useEffect(() => {
    if (isAuthenticated && !user?.must_change_password && !showPasswordChangeModal) {
      navigate(returnUrl, { replace: true })
    }
  }, [isAuthenticated, user?.must_change_password, showPasswordChangeModal, navigate, returnUrl])
  
  /**
   * Validate email format
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }
  
  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {}
    
    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    // Clear previous errors
    setErrors({})
    
    // Validate form
    if (!validateForm()) {
      return
    }
    
    setIsLoading(true)

    try {
      // Store the password in ref for password change modal (not in state to avoid exposure)
      loginPasswordRef.current = password

      await login(email, password)

      // Success - toast and redirect will be handled by useEffect if password change is needed
      // Otherwise redirect immediately
      toast.success('Login successful!')
    } catch (error) {
      // Handle login errors
      const errorMessage = normalizeErrorMessage(error, 'Login failed. Please try again.')
      
      // Check for specific error types
      if (errorMessage.includes('Invalid email or password')) {
        setErrors({
          email: 'Invalid email or password',
          password: 'Invalid email or password'
        })
        toast.error('Invalid email or password')
      } else if (errorMessage.includes('Account is inactive')) {
        toast.error('Your account is inactive. Please contact support.')
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle successful password change
   */
  const handlePasswordChangeSuccess = async () => {
    setShowPasswordChangeModal(false)
    toast.success('Password changed! Redirecting...')

    try {
      await reloadUser()
    } catch {
      // Non-critical — redirect will still work
    }

    navigate(returnUrl, { replace: true })
  }

  return (
    <>
      {/* Password Change Modal */}
      {showPasswordChangeModal && accessToken && loginPasswordRef.current && (
        <ChangePasswordModal
          currentPassword={loginPasswordRef.current}
          accessToken={accessToken}
          onSuccess={handlePasswordChangeSuccess}
          canClose={false}
        />
      )}


    <div className="min-h-screen flex items-center justify-center bg-theme-soft">
      <div className="max-w-md w-full mx-4">
        {/* Login Card */}
        <div className="auth-card">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="auth-icon-shell">
              <LogIn className="w-8 h-8 text-ink" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-ink">
              Welcome Back
            </h1>
            <p className="text-sm text-ink-soft">
              Sign in to your account to continue
            </p>
          </div>
          
          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2 text-ink">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-ink-soft" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (errors.email) setErrors({ ...errors, email: undefined })
                  }}
                  className={`auth-input ${
                    errors.email
                      ? 'border-red-500 focus:ring-red-500'
                      : 'auth-input-default'
                  }`}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email}
                </p>
              )}
            </div>
            
            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2 text-ink">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-ink-soft" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    if (errors.password) setErrors({ ...errors, password: undefined })
                  }}
                  className={`auth-input ${
                    errors.password
                      ? 'border-red-500 focus:ring-red-500'
                      : 'auth-input-default'
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.password}
                </p>
              )}
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="auth-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>
          
          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-ink-soft">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium hover:underline text-ink"
              >
                Register with invitation
              </Link>
            </p>
          </div>
        </div>
        
        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-ink-soft">
            Need help? Contact your administrator for an invitation.
          </p>
        </div>
      </div>
    </div>
    </>
  )
}

export default LoginPage
